import { useRef, useEffect } from "react";

// Animated volumetric "moving head" light beams on a canvas.
// Beams emit from the top, sweep over time, react to the cursor, and converge
// into a flare at the base — echoing the Vision Studio logo.
export default function LightBeams({ className = "", intensity = 1 }) {
  const canvasRef = useRef(null);
  const mouse = useRef({ x: 0.5, y: 0.35 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf, W = 0, H = 0, dpr = 1;

    const beams = [
      { baseX: 0.22, angle: (100 * Math.PI) / 180, swing: 0.22, speed: 0.00028, phase: 0.0, hue: 255 },
      { baseX: 0.78, angle: (80 * Math.PI) / 180, swing: 0.22, speed: 0.00032, phase: 2.1, hue: 255 },
      { baseX: 0.5, angle: (90 * Math.PI) / 180, swing: 0.12, speed: 0.00045, phase: 4.0, hue: 235 },
      { baseX: 0.4, angle: (95 * Math.PI) / 180, swing: 0.3, speed: 0.00022, phase: 5.4, hue: 255 },
    ];

    const particles = Array.from({ length: 80 }, () => ({
      x: Math.random(),
      y: Math.random(),
      r: Math.random() * 1.6 + 0.3,
      s: Math.random() * 0.00012 + 0.00003,
    }));

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      W = canvas.clientWidth;
      H = canvas.clientHeight;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const onMove = (e) => {
      mouse.current.x = e.clientX / window.innerWidth;
      mouse.current.y = e.clientY / window.innerHeight;
    };
    window.addEventListener("mousemove", onMove);

    const draw = (t) => {
      ctx.clearRect(0, 0, W, H);
      ctx.globalCompositeOperation = "lighter";

      beams.forEach((b) => {
        const ox = b.baseX * W;
        const oy = -30;
        const aim =
          b.angle +
          Math.sin(t * b.speed + b.phase) * b.swing +
          (mouse.current.x - 0.5) * 0.28;
        const len = H * 1.35;
        const tx = ox + Math.cos(aim) * len;
        const ty = oy + Math.sin(aim) * len;
        const spread = 95;
        const nx = Math.cos(aim + Math.PI / 2);
        const ny = Math.sin(aim + Math.PI / 2);

        const grad = ctx.createLinearGradient(ox, oy, tx, ty);
        const a = 0.26 * intensity;
        grad.addColorStop(0, `rgba(${b.hue},${b.hue},${b.hue},${a})`);
        grad.addColorStop(0.45, `rgba(210,215,225,${0.05 * intensity})`);
        grad.addColorStop(1, "rgba(255,255,255,0)");
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.moveTo(ox - 7, oy);
        ctx.lineTo(tx + nx * spread, ty + ny * spread);
        ctx.lineTo(tx - nx * spread, ty - ny * spread);
        ctx.lineTo(ox + 7, oy);
        ctx.closePath();
        ctx.fill();

        // source flare
        const g2 = ctx.createRadialGradient(ox, oy, 0, ox, oy, 70);
        g2.addColorStop(0, `rgba(255,255,255,${0.5 * intensity})`);
        g2.addColorStop(1, "rgba(255,255,255,0)");
        ctx.fillStyle = g2;
        ctx.beginPath();
        ctx.arc(ox, oy, 70, 0, Math.PI * 2);
        ctx.fill();
      });

      // drifting dust
      particles.forEach((p) => {
        p.y -= p.s * 16;
        if (p.y < 0) {
          p.y = 1;
          p.x = Math.random();
        }
        ctx.fillStyle = `rgba(255,255,255,${(0.05 + p.r * 0.05) * intensity})`;
        ctx.beginPath();
        ctx.arc(p.x * W, p.y * H, p.r, 0, Math.PI * 2);
        ctx.fill();
      });

      // base convergence flare
      const fx = W * 0.5;
      const fy = H * 0.92;
      const pulse = 150 + Math.sin(t * 0.0018) * 24;
      const fg = ctx.createRadialGradient(fx, fy, 0, fx, fy, pulse);
      fg.addColorStop(0, `rgba(255,255,255,${0.16 * intensity})`);
      fg.addColorStop(1, "rgba(255,255,255,0)");
      ctx.fillStyle = fg;
      ctx.beginPath();
      ctx.arc(fx, fy, pulse, 0, Math.PI * 2);
      ctx.fill();

      ctx.globalCompositeOperation = "source-over";
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMove);
    };
  }, [intensity]);

  return <canvas ref={canvasRef} className={className} />;
}
